import { Empty, Tooltip } from "antd";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useEffect, useMemo, useRef, useState } from "react";
import { faBoxesPacking, faEllipsis, faFlask, faHouse, faLeaf, faPlantWilt, faPlus, faRecycle, faSeedling, faSprayCan, faWheatAlt } from "@fortawesome/free-solid-svg-icons";

//ASSETS
import iconTalhao from '../../../../assets/img/iconTalhao.svg';

//APP
import secureLocalStorage from "react-secure-storage";
import { localConfig } from "../../../../Configs/localConfig";
import { useGetOsColetada } from "../../../../services/Laboratorio/GET";
import { getCodTipoColeta, TipoColeta } from "./meta";
import { montarRelatorioSolo, montarTotaisSolo } from "./modulos/solo/meta/relatorio";
import { motivoSemPermissao, usePermissoesOs } from "./meta/usePermissoesOs";
import { Item__Talhao, useGetPropriedadesDoCliente } from "../../../../services/atividades/GET";

//COMPONENTS
import Input from "../../../../Components/CustomUi/input";
import ButtonCustom from "../../../../Components/ButtonCustom";
import GeneratePdfButton from "../../../../Components/relatorio-component";
import { Render } from "../../../../Components/CustomUi/render";
import { Inline } from "../../../../Components/CustomUi/inline";
import { PopSelect } from "../../../../Components/CustomUi/PopSelect";
import { DefautContainer } from "../../../../Components/CustomUi/defaultContainer";
import { Solo, SoloHandle } from "./modulos/solo";

//CONSTs
const ICON_LIST_TALHAO = ["kml", "coordenadas"];
const TABS_CONFIG = [
    { descricao: 'Solo', icone: faWheatAlt, peso: 1 },
    { descricao: 'Plantas', icone: faSeedling, peso: 1.2 },
    { descricao: 'Folhas', icone: faLeaf, peso: 1.1 },
    { descricao: 'Raízes', icone: faPlantWilt, peso: 1.1 },
    { descricao: 'Microbiológica', icone: faFlask, peso: 1.7 },
    { descricao: 'Fertilizantes', icone: faBoxesPacking, peso: 1.5 },
    { descricao: 'Corretivos', icone: faSprayCan, peso: 1.3 },
    { descricao: 'Composto orgânico', icone: faRecycle, peso: 1.9 },
    { descricao: 'Outro', icone: faEllipsis, peso: 1 },
]

//#region APP
export const NovoModuloOsParaColeta: React.FC = () => {

    //REFS DOS SUBMÓDULOS (cada tipo de coleta é dono da própria gaveta)
    const soloRef = useRef<SoloHandle>(null);

    //CONST
    const { codSafra } = localConfig();

    //REF
    const containerRef = useRef<HTMLDivElement>(null)

    //#region STATES
    //PROPRIEDADE
    const [propriedade_selecionada, setPropriedade_selecionada] = useState<string>('');
    const [propriedade_selecionada_id, setPropriedade_selecionada_id] = useState<number | string>(null);
    //TALHÃO
    const [lista_talhoes, setLista_talhoes] = useState<Item__Talhao[]>([]);
    const [talhao_selecionado, setTalhao_selecionado] = useState<string>('');
    const [talhao_selecionado_id, setTalhao_selecionado_id] = useState<number | string>(null);
    //BUSCAR
    const [buscar_tabela, setBuscar_tabela] = useState<string>('');
    // Valor "atrasado" da busca: é ele que entra na queryKey, para não disparar
    // uma requisição a cada tecla digitada.
    const [buscar_debounced, setBuscar_debounced] = useState<string>('');
    //TIPO DE COLETA
    const [tipo_coleta, setTipo_coleta] = useState<TipoColeta>('Solo');
    //CONTAINER
    const [containerWidth, setContainerWidth] = useState<number>(0);
    // -1 = todos os registros. Uma propriedade raramente passa de 15 OS, e trazer
    // tudo de uma vez é o que permite ordenar a lista inteira (e não só a página).
    const [pagination, setPagination] = useState<number>(-1);
    const [page, setPage] = useState<number>(1);
    // Loading curto e forçado na troca de página: quando a página vem do cache
    // não há requisição, mas a re-renderização das linhas ainda custa alguns
    // frames. Sem nenhum sinal em tela isso é percebido como travada.
    const [fake_loading, setFake_loading] = useState<boolean>(false);


    // Código do tipo de coleta ativo no backend (Solo=282; demais tipos conforme liberados).
    const cod_tipo_coleta = getCodTipoColeta(tipo_coleta) ?? undefined;

    /** Permissões da rotina de OS — controlam "Novo" aqui e as ações nos submódulos. */
    const permissoes = usePermissoesOs();

    //#region API
    const { data_propriedades, carregando_propriedades } = useGetPropriedadesDoCliente(); //lista as propriedades com os talhões
    const { data_osColeta, carregando_osColeta, buscando_osColeta, refetch_data_osColeta } = useGetOsColetada(pagination, page, buscar_debounced, codSafra, Number(propriedade_selecionada_id), cod_tipo_coleta);

    // Memoiza para não recriar arrays a cada render (quebraria o memo do PopSelect/SvgTalhaoMemo)
    const propriedades_ativas = useMemo(
        () => data_propriedades?.filter(i => i.status === 1),
        [data_propriedades]
    );
    const talhoes_filtrados = useMemo(
        () => (lista_talhoes ?? []).filter(i => i?.cod_safra == codSafra && i.cod_talhao === null),
        [lista_talhoes, codSafra]
    );
    // A busca por nome é feita no servidor (param `nome`); aqui só resta o filtro
    // por talhão, que a API não suporta.
    const lista_os_filtrada = useMemo(
        () => (data_osColeta?.itens ?? [])
            .filter(i => !talhao_selecionado || i.talhao_nome === talhao_selecionado),
        [data_osColeta, talhao_selecionado]
    );


    //#region RELATÓRIO PDF
    /**
     * O relatório cobre TODAS as OS da propriedade (sem o filtro de talhão) e é
     * montado pelo arquivo de relatório do submódulo ativo — cada tipo de coleta
     * declara as próprias colunas, sem condicional acumulada aqui.
     */
    const lista_os_relatorio = useMemo(() => data_osColeta?.itens ?? [], [data_osColeta])

    const dados_relatorio = useMemo(() => {
        switch (tipo_coleta) {
            case 'Solo':
                return montarRelatorioSolo(lista_os_relatorio)
            default:
                return []
        }
    }, [tipo_coleta, lista_os_relatorio])

    const totais_relatorio = useMemo(() => {
        switch (tipo_coleta) {
            case 'Solo':
                return montarTotaisSolo(lista_os_relatorio)
            default:
                return []
        }
    }, [tipo_coleta, lista_os_relatorio])

    /** Subtítulo do PDF: cliente + propriedade (que saiu das colunas por se repetir). */
    const subtitulo_relatorio = useMemo(() => {
        const cliente = lista_os_relatorio?.[0]?.nome_cliente ?? ''
        return [cliente, propriedade_selecionada].filter(Boolean).join(' - ')
    }, [lista_os_relatorio, propriedade_selecionada])

    /** Produção libera o relatório apenas para acesso do tipo "conta". */
    const pode_gerar_relatorio = secureLocalStorage.getItem('tipo-acesso') === 'conta'

    //#region FUNCTIONS
    /**
     * Abre a gaveta de "nova OS" do submódulo correspondente ao tipo de coleta ativo.
     * Cada submódulo é dono da própria gaveta e expõe `abrirNovo()` via ref.
     * Preparado para escalar: basta registrar o ref + case do novo tipo.
     */
    function abrirFormularioNovaOs() {
        switch (tipo_coleta) {
            case 'Solo':
                abrirFormSolo();
                break;
            // case 'Plantas': abrirFormPlanta(); break;
            // case 'Folhas': abrirFormFolha(); break;
            // ...demais submódulos conforme forem implementados
            default:
                break;
        }
    }

    function abrirFormSolo() {
        soloRef.current?.abrirNovo();
    }

    useEffect(() => {
        const updateWidth = () => {
            if (containerRef.current) {
                setContainerWidth(containerRef.current.offsetWidth)
            }
        }
        updateWidth()
        window.addEventListener('resize', updateWidth)
        return () => window.removeEventListener('resize', updateWidth)
    }, []);

    useEffect(() => {
        setTalhao_selecionado(null);

        /**obtem a lista de talhões da propriedade selecionada */
        if ((data_propriedades ?? []).length > 0) {
            let propriedade_selecionada = (data_propriedades ?? []).filter(i => i?.cod === propriedade_selecionada_id);
            setLista_talhoes(propriedade_selecionada?.[0]?.talhao ?? []);
        }
    }, [propriedade_selecionada_id]);

    // Aguarda a digitação parar antes de levar a busca para a queryKey.
    useEffect(() => {
        const timer = setTimeout(() => setBuscar_debounced(buscar_tabela), 500);
        return () => clearTimeout(timer);
    }, [buscar_tabela]);

    // Volta para a primeira página sempre que o recorte da consulta muda,
    // senão a página atual pode não existir no novo resultado.
    useEffect(() => {
        setPage(1);
    }, [propriedade_selecionada_id, tipo_coleta, buscar_debounced]);

    // Sem efeito de refetch manual: propriedade, tipo de coleta, busca e
    // paginação fazem parte da queryKey, então o React Query busca sozinho e
    // reaproveita o cache das páginas já visitadas.

    // Mostra o loading por um instante ao paginar. Não é o tempo da requisição
    // (páginas em cache não fazem nenhuma) e sim o custo de montar as linhas,
    // que sem feedback nenhum parece travamento.
    const primeira_renderizacao = useRef<boolean>(true);
    useEffect(() => {
        if (primeira_renderizacao.current) {
            primeira_renderizacao.current = false;
            return;
        }
        setFake_loading(true);
        const timer = setTimeout(() => setFake_loading(false), 400);
        return () => clearTimeout(timer);
    }, [page, pagination]);


    /**
    * Calcula a largura de cada tab proporcionalmente
    * para preencher 100% da largura do container.
    * O padding interno do ListTabs (10px = 5px * 2) é descontado.
    */
    function calcularLarguras() {
        const larguraDisponivel = containerWidth - 10 // padding do ListTabs
        const somaPesos = TABS_CONFIG.reduce((acc, tab) => acc + tab.peso, 0)

        return TABS_CONFIG.map((tab) => ({
            descricao: tab.descricao,
            icone: tab.icone,
            width: Math.floor((tab.peso / somaPesos) * larguraDisponivel),
        }))
    }

    // const tabsComLargura = calcularLarguras()

    //#region APP
    return (
        <DefautContainer
            title="Os para coleta"
            id_safra="precisao/os-para-coleta"
        >
            <Inline alignItems="flex-end" refProp={containerRef}>
                <div style={{ flex: '0 1 auto', minWidth: 0, maxWidth: '25%' }}>
                    <PopSelect
                        label='Propriedade'
                        empty_placeholder='Selecione a propriedade'
                        value={propriedade_selecionada}
                        icon={faHouse}
                        onChange={setPropriedade_selecionada}
                        onChangeId={setPropriedade_selecionada_id}
                        items={propriedades_ativas}
                        descricaoItem={'nome'}
                        idItem={'cod'}
                        loading={carregando_propriedades}
                    />
                </div>

                <Tooltip title={propriedade_selecionada_id === null ? 'Selecione uma propriedade primeiramente' : undefined}><samp style={{ flex: '0 1 auto', minWidth: 0, maxWidth: '25%', display: 'block' }}>
                    <PopSelect
                        label="Selecione o talhão"
                        empty_placeholder="Nenhum foi selecionado"
                        icon={iconTalhao}
                        value={talhao_selecionado}
                        onChange={setTalhao_selecionado}
                        onChangeId={setTalhao_selecionado_id}
                        items={talhoes_filtrados}
                        iconList={ICON_LIST_TALHAO}
                        maxPontosSvg={500}
                        descricaoItem="nome"
                        idItem="cod"
                        extraItem='area'
                        disabled={propriedade_selecionada_id === null}
                        zIndex={9}
                        loading={carregando_propriedades}
                    /></samp>
                </Tooltip>

                <div style={{ flex: '1 1 0', minWidth: 180 }}>
                    <Input
                        placeholder='Buscar OS'
                        value={buscar_tabela}
                        onChange={setBuscar_tabela}
                        desabilitado={propriedade_selecionada_id === null}
                    />
                </div>

                {/* Sem permissão o botão fica desabilitado (não oculto): o tooltip
                    explica o motivo em vez de sumir com a ação. */}
                <Tooltip title={motivoSemPermissao(permissoes?.inclusao !== 0, 'criar OS')}>
                    <span style={{ marginBottom: 7, display: 'inline-block' }}>
                        <ButtonCustom
                            icon={<FontAwesomeIcon icon={faPlus} />}
                            onClick={abrirFormularioNovaOs}
                            disabled={propriedade_selecionada_id === null || permissoes?.inclusao === 0}
                        >
                            Novo
                        </ButtonCustom>
                    </span>
                </Tooltip>

                <Render condition={pode_gerar_relatorio}>
                    <div style={{ marginBottom: 7 }}>
                        <GeneratePdfButton
                            title='Relatório O.S'
                            titleSecundario={subtitulo_relatorio}
                            dados={dados_relatorio}
                            totais={totais_relatorio}
                            disable={propriedade_selecionada_id === null || lista_os_relatorio.length === 0}
                        />
                    </div>
                </Render>
            </Inline>

            {/* <Tabs
                selecionado={tipo_coleta}
                onChange={setTipo_coleta}
                tabs={tabsComLargura}
                useTabWidth
            /> */}

            {/* Estado vazio de nível de página: nenhuma propriedade selecionada.
                O "nenhuma OS cadastrada" agora é responsabilidade de cada submódulo. */}
            {(propriedade_selecionada_id == null) && (
                <Empty
                    style={{ marginTop: 100 }}
                    image={Empty.PRESENTED_IMAGE_SIMPLE}
                    description={<h4 style={{ color: `var(--text-color-light)` }}>Selecione uma propriedade!</h4>}
                />
            )}

            {/* Cada submódulo é dono da própria tabela, estado vazio e gaveta.
                Sempre monta (quando há propriedade) para o botão "Novo" conseguir abrir a gaveta. */}
            {(tipo_coleta === 'Solo' && propriedade_selecionada_id !== null) && (
                <Solo
                    ref={soloRef}
                    lista_os={lista_os_filtrada}
                    refetch_data_osColeta={refetch_data_osColeta}
                    loading={carregando_osColeta}
                    buscando={buscando_osColeta || fake_loading}
                    total={data_osColeta?.total ?? 0}
                    total_filtrado={talhao_selecionado ? lista_os_filtrada.length : 0}
                    page={page}
                    setPage={setPage}
                    setPagination={setPagination}
                />
            )}

        </DefautContainer>
    )
}
//#endregion